<template>
  <div class="dropdown" :class="{ touch_target: touchTarget }" ref="target">
    <div
      class="dropdown_trigger"
      ref="trigger"
      role="button"
      tabindex="0"
      :aria-expanded="state.isActive"
      aria-haspopup="menu"
      :aria-label="triggerAriaLabel || undefined"
      :data-test="triggerDataTest || undefined"
      @click.stop.prevent="toggle()"
      @keydown.enter.stop.prevent="toggle(true)"
      @keydown.space.stop.prevent="toggle(true)"
      @keydown.esc.stop.prevent="close(true)"
    >
      <template v-if="displayTitle">
        <div class="dropdown_title">
          <span v-if="state.activeItem && !state.activeItem.reset">
            {{ state.activeItem.title }}
          </span>
          <span v-else>
            {{ placeholder }}
          </span>
          <span class="material-symbols-outlined">arrow_drop_down</span>
        </div>
      </template>
      <slot></slot>
    </div>

    <div
      ref="menu"
      class="dropdown_menu"
      :class="{ active: state.isActive, above: opensAbove }"
      role="menu"
    >
      <div class="dropdown_content">
        <button
          type="button"
          :class="{
            dropdown_item: true,
            active:
              state.activeItem &&
              !state.activeItem.reset &&
              state.activeItem.value === item.value,
            dangerous: item.destructive,
            disabled: item.disabled,
          }"
          v-for="(item, index) of items"
          :key="index"
          :disabled="item.disabled"
          role="menuitem"
          @click.stop="select(item)"
          @keydown.enter.stop.prevent="select(item, true)"
          @keydown.space.stop.prevent="select(item, true)"
          @keydown.tab.stop.prevent="close(true)"
          @keydown.esc.stop.prevent="close(true)"
          @keydown="navigateItems"
        >
          {{ item.title }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { reactive, nextTick, onBeforeUnmount, PropType, watch, ref } from "vue";
import { onClickOutside } from "@vueuse/core";

interface Item {
  value: string | null;
  title: string;
  reset?: boolean;
  destructive?: boolean;
  disabled?: boolean;
}

interface Props {
  items: Array<Item>;
  displayTitle: boolean;
  placeholder: string;
}

interface ComponentState {
  isActive: boolean;
  activeItem: Item | null;
}

const props = defineProps({
  items: {
    type: Array as PropType<Array<Item>>,
    required: true,
  },
  displayTitle: {
    type: Boolean,
    required: false,
    default: false,
  },
  placeholder: {
    type: String,
    required: false,
    default: "",
  },
  value: {
    type: String,
    required: false,
  },
  triggerAriaLabel: {
    type: String,
    required: false,
    default: "",
  },
  triggerDataTest: {
    type: String,
    required: false,
    default: "",
  },
  fitViewport: {
    type: Boolean,
    default: false,
  },
  touchTarget: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emits = defineEmits(["change"]);
const target = ref<HTMLElement>();
const trigger = ref<HTMLElement>();
const menu = ref<HTMLElement>();
const opensAbove = ref(false);

const state: ComponentState = reactive({
  isActive: false,
  activeItem:
    props.value && props.items
      ? (props.items.find((i: Item) => i.value === props.value) ?? null)
      : null,
});

// Keep `activeItem` in sync when either the selected value changes OR the
// items list loads/changes (items are often async).
watch([() => props.value, () => props.items], ([newVal]) => {
  state.activeItem = props.items.find((i: Item) => i.value === newVal) ?? null;
});

function close(restoreFocus = false) {
  state.isActive = false;
  state.activeItem =
    props.value && props.items
      ? (props.items.find((i: Item) => i.value === props.value) ?? null)
      : null;

  if (restoreFocus) {
    trigger.value?.focus();
  }
}

async function toggle(focusFirstItem = false) {
  state.isActive = !state.isActive;
  if (!state.isActive) return;
  opensAbove.value = false;
  await nextTick();
  if (!state.isActive) return;
  if (props.fitViewport) {
    const bounds = menu.value?.getBoundingClientRect();
    const triggerBounds = trigger.value?.getBoundingClientRect();
    opensAbove.value = !!(
      bounds &&
      triggerBounds &&
      bounds.bottom > window.innerHeight &&
      triggerBounds.top >= bounds.height
    );
    await nextTick();
    if (!state.isActive) return;
  }
  if (focusFirstItem) {
    target.value
      ?.querySelector<HTMLButtonElement>(".dropdown_item:not(:disabled)")
      ?.focus();
  }
}

function navigateItems(event: KeyboardEvent) {
  if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;

  const items = Array.from(
    target.value?.querySelectorAll<HTMLButtonElement>(
      ".dropdown_item:not(:disabled)",
    ) ?? [],
  );
  if (!items.length) return;

  event.stopPropagation();
  event.preventDefault();
  const currentIndex = items.indexOf(event.currentTarget as HTMLButtonElement);
  const nextIndex =
    event.key === "Home"
      ? 0
      : event.key === "End"
        ? items.length - 1
        : event.key === "ArrowDown"
          ? (currentIndex + 1) % items.length
          : (currentIndex - 1 + items.length) % items.length;
  items[nextIndex]?.focus();
}

function select(item: Item, restoreFocus = true) {
  if (item.disabled) {
    return;
  }
  state.activeItem = item;
  close(restoreFocus);
  emits("change", item.value, item.reset);
}

const closeFromWindowEvent = () => close();

onClickOutside(target, (event) => {
  close();
});

window.addEventListener("pvdrag", closeFromWindowEvent);

onBeforeUnmount(() => {
  window.removeEventListener("pvdrag", closeFromWindowEvent);
});
</script>

<style lang="scss" scoped>
.dropdown {
  display: inline-flex;
  position: relative;
  vertical-align: top;
  font-size: 0.9rem;
}

.dropdown_menu {
  display: none;
  left: 0;
  min-width: 7rem;
  padding-top: 4px;
  position: absolute;
  top: 80%;
  z-index: 20;
}

.dropdown.right_align .dropdown_menu {
  left: auto;
  right: 0;
}

.dropdown.right .dropdown_menu {
  left: auto;
  right: 0;
}

.dropdown.top .dropdown_menu {
  top: -180%;
}

.dropdown_menu.above {
  top: auto;
  bottom: 100%;
  padding-top: 0;
  padding-bottom: 4px;
}

.dropdown_trigger {
  cursor: pointer;
}

.dropdown_trigger[data-test]:focus-visible {
  border-radius: 4px;
  outline: 2px solid var(--classic-accent, #34b5ba);
  outline-offset: 2px;
}

.dropdown_trigger .dropdown_title,
.dropdown_trigger :deep(.dropdown_title) {
  padding: 0.35rem;
  cursor: pointer;
  justify-content: center;
  text-align: center;
  white-space: nowrap;
  padding: 0.35rem 1.2em;
  background-color: var(--classic-surface, #fff);
  color: var(--classic-text, inherit);
  box-shadow: none;
  display: inline-flex;
  line-height: 1.5;
  min-height: 1.5em;
  font-size: 1em;
  border-radius: 3px;
  border: 1px solid var(--classic-border-strong, #d0d4d9);
  align-items: center;
}

.dropdown.inline .dropdown_trigger .dropdown_title {
  border: none;
}

.dropdown_trigger :deep(.material-symbols-outlined) {
  font-size: 1.3em;
  vertical-align: middle;
}

.dropdown_trigger :deep(.material-symbols-outlined.circle) {
  font-size: 1.3em;
  color: var(--classic-accent, #34b5ba);
  border: 1px solid var(--classic-accent, #34b5ba);
  border-radius: 50%;
  padding: 0.2em;
}
.dropdown_menu.active {
  display: block;
}

.dropdown_content {
  background-color: var(--classic-surface-raised, #fff);
  border-radius: 4px;
  box-shadow:
    0 0.5em 1em -0.125em var(--classic-shadow, rgba(10, 10, 10, 0.1)),
    0 0 0 1px var(--classic-border-subtle, rgba(10, 10, 10, 0.02));
  padding-bottom: 0.5rem;
  padding-top: 0.5rem;
}

.dropdown_item {
  color: var(--classic-text, #4a4a4a);
  display: block;
  font-size: 0.875rem;
  line-height: 1.5;
  padding: 0.375rem 1rem;
  position: relative;
  cursor: pointer;
}

.dropdown_item.active {
  color: var(--classic-accent, #34b5ba);
}

.dropdown_item:hover:not(:disabled),
.dropdown_item:focus-visible {
  background-color: var(--classic-surface-inset, #f5f5f5);
}

.dropdown_item.dangerous {
  color: var(--classic-danger, #cf453e);
}

.dropdown_item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

button.dropdown_item {
  appearance: none;
  background: transparent;
  border: 0;
  font-family: inherit;
  padding-right: 3rem;
  text-align: inherit;
  white-space: nowrap;
  width: 100%;
}

@media (max-width: 767px), (any-pointer: coarse) {
  .dropdown.touch_target .dropdown_trigger {
    align-items: center;
    display: inline-flex;
    justify-content: center;
    min-height: 44px;
    min-width: 44px;
  }

  .dropdown.touch_target .dropdown_item {
    align-items: center;
    display: flex;
    min-height: 44px;
  }
}
</style>
